import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Stage,
    Layer,
    Image as KImage,
    Line,
    Transformer
} from "react-konva";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLanguage } from '@/lib/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ================= IMAGE HOOK =================

function useImage(url) {
    const [state, setState] = useState({ img: null, w: 0, h: 0 });

    useEffect(() => {
        if (!url) return;

        const image = new window.Image();
        image.crossOrigin = "anonymous";
        image.src = url;

        image.onload = () =>
            setState({
                img: image,
                w: image.naturalWidth || 0,
                h: image.naturalHeight || 0,
            });

    }, [url]);

    return state; // { img, w, h }
}


// ================= EXISTING IMG HOOK =================

function ExistingImage({ sig }) {
    const { img } = useImage(sig.assetUrl);
    if (!img) return null;

    return (
        <KImage
            image={img}
            x={sig.x}
            y={sig.y}
            width={sig.width}
            height={sig.height}
            opacity={0.6}
        />
    );
}


// ================= PAGE =================

export default function WallInvite() {

    const { token } = useParams();
    const { t } = useLanguage();

    const navigate = useNavigate();

    const stageRef = useRef(null);
    const drawLayerRef = useRef(null);
    const imgRef = useRef();
    const trRef = useRef();

    const [valid, setValid] = useState(null);
    const [artistName, setArtistName] = useState("");
    const [imageUrl, setImageUrl] = useState("");

    const [existing, setExisting] = useState([]);

    const WALL_W = 1000;
    const WALL_H = 1000;

    const [sig, setSig] = useState({
        x: 200,
        y: 200,
        width: 300,
        height: 150, // fallback (oran hesaplanamazsa)
    });

    const { img, w, h } = useImage(imageUrl);

    const isMobile = window.innerWidth < 968;
    // ================= DRAW =================

    const [lines, setLines] = useState([]);
    const [drawing, setDrawing] = useState(false);

    const [tool, setTool] = useState("pen");
    const [color, setColor] = useState("#000000");
    const [size, setSize] = useState(3);


    const handleMouseDown = (e) => {
        setDrawing(true);
        const pos = e.target.getStage().getPointerPosition();

        setLines(prev => [
            ...prev,
            {
                tool,
                color,
                size,
                points: [pos.x, pos.y],
            }
        ]);
    };


    const handleMouseMove = (e) => {
        if (!drawing) return;

        const stage = e.target.getStage();
        const point = stage.getPointerPosition();

        setLines(prev => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            last.points = last.points.concat([point.x, point.y]);
            return copy;
        });
    };


    const handleMouseUp = () => setDrawing(false);


    // ================= TOKEN =================

    useEffect(() => {
        axios
            .get(`${API}/wall/invite/${token}`)
            .then(() => setValid(true))
            .catch(() => setValid(false));
    }, [token]);


    // ================= EXISTING =================

    useEffect(() => {
        axios
            .get(`${API}/wall/main/signatures`)
            .then(res => setExisting(res.data));
    }, []);


    // ================= SUBMIT =================

    const submit = async () => {

        if (!artistName) {
            alert(t('wall_invite_artist_name_required'));
            return;
        }

        if (!imageUrl && lines.length === 0) {
            alert(t('wall_invite_export_failed'));
            return;
        }

        // ✅ SADECE DRAW LAYER EXPORT (existing karışmaz)
        const drawLayer = drawLayerRef.current;
        drawLayer.draw();

        const dataUrl = drawLayer.toDataURL({
            pixelRatio: 2,
        });

        if (!dataUrl || dataUrl.length < 100) {
            alert(t('wall_invite_export_failed'));
            return;
        }

        await axios.post(`${API}/wall/signatures`, {
            token,
            artistName,
            assetUrl: dataUrl,

            // 🔥 CANVAS FULL EXPORT
            x: 0,
            y: 0,
            width: 1000,
            height: 1000,
            rotation: 0,
        });

        alert(t('wall_invite_submitted'));
        navigate("/izbirak");
    };



    if (valid === null) return <div className="pt-32">{t('wall_invite_loading')}</div>;
    if (!valid) return <div className="pt-32 text-center">{t('wall_invite_invalid_link')}</div>;


    return (
        <main className="pt-32 pb-24">
            {isMobile ? (

                <div className="text-center py-32 space-y-4">
                    <h2 className="text-2xl font-bold px-16">
                        {t('wall_invite_mobile_warning')}
                    </h2>

                    <p className="text-white/60">
                        {t('wall_invite_mobile_warning_2')}
                    </p>
                </div>

            ) : (
                <div className="max-w-6xl mx-auto px-6 space-y-6">

                    <h1 className="text-3xl font-bold">
                        {t('wall_invite_title')}
                    </h1>

                    <div className="
  w-full
  max-w-5xl
  bg-white/[0.02]
  border border-white/10
  rounded-2xl
  p-6
  backdrop-blur-xl
  space-y-6
">

                        {/* INPUTS */}
                        <div className="grid md:grid-cols-2 gap-4">

                            <div className="space-y-2">
                                <label className="text-xs text-white/40">
                                    {t('wall_invite_artist_name_placeholder')}
                                </label>

                                <input
                                    value={artistName}
                                    onChange={(e) => setArtistName(e.target.value)}
                                    className="
          w-full
          bg-black/40
          border border-white/10
          rounded-xl
          px-4 py-3
          text-sm
          outline-none
          focus:border-white/30
          transition
        "
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs text-white/40">
                                    {t('wall_invite_png_url_placeholder')}
                                </label>

                                <input
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    className="
          w-full
          bg-black/40
          border border-white/10
          rounded-xl
          px-4 py-3
          text-sm
          outline-none
          focus:border-white/30
          transition
        "
                                />
                            </div>

                        </div>


                        {/* BRUSH SETTINGS */}
                        <div className="flex flex-wrap items-center gap-8">

                            {/* COLOR */}
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-white/40">
                                    {t('wall_invite_color_label')}
                                </span>
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    className="
    w-10 h-10
    p-0
    bg-transparent
    border border-white/20
    rounded-full
    cursor-pointer

    appearance-none
    [&::-webkit-color-swatch]:border-0
    [&::-webkit-color-swatch]:rounded-full
    [&::-webkit-color-swatch]:p-0

    [&::-webkit-color-swatch-wrapper]:p-0

    [&::-moz-color-swatch]:border-0
    [&::-moz-color-swatch]:rounded-full
  "
                                />
                            </div>


                            {/* SIZE */}
                            <div className="flex items-center gap-4 flex-1 min-w-[220px]">

                                <span className="text-xs text-white/40 whitespace-nowrap">
                                    {t('wall_invite_brush_size_label')}
                                </span>

                                <input
                                    type="range"
                                    min="1"
                                    max="20"
                                    value={size}
                                    onChange={(e) => setSize(e.target.value)}
                                    className="
          w-full
          accent-white
        "
                                />
                            </div>

                        </div>


                        {/* ACTIONS */}
                        <div className="flex items-center justify-between flex-wrap gap-4">

                            {/* TOOLS */}
                            <div className="flex gap-2">

                                <button
                                    onClick={() => setTool("pen")}
                                    className={`
          px-5 py-2.5
          rounded-xl
          border
          text-sm
          transition
          ${tool === "pen"
                                            ? "bg-white text-black border-white"
                                            : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"}
        `}
                                >
                                    {t('wall_invite_pen_tool')}
                                </button>


                                <button
                                    onClick={() => setTool("eraser")}
                                    className={`
          px-5 py-2.5
          rounded-xl
          border
          text-sm
          transition
          ${tool === "eraser"
                                            ? "bg-white text-black border-white"
                                            : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"}
        `}
                                >
                                    {t('wall_invite_eraser_tool')}
                                </button>


                                <button
                                    onClick={() => setLines([])}
                                    className="
          px-5 py-2.5
          rounded-xl
          border border-white/15
          text-sm
          text-white/70
          hover:border-red-700
          hover:text-white
          transition
        "
                                >
                                    {t('wall_invite_clear_tool')}
                                </button>

                            </div>


                            {/* SUBMIT */}
                            <button
                                onClick={submit}
                                className="
        bg-white
        text-black
        px-7 py-2.5
        rounded-xl
        text-sm
        font-medium
        hover:opacity-90
        transition
      "
                            >
                                {t('wall_invite_submit_button')}
                            </button>

                        </div>

                    </div>

                    <p>{t('wall_invite_draw_or_upload')}</p>

                    {/* WALL */}
                    <div
                        className="border border-white/10 bg-white"
                        style={{ width: 1000, height: 1000, overflow: "hidden", touchAction: "none" }}
                    >
                        <Stage
                            ref={stageRef}
                            width={WALL_W}
                            height={WALL_H}
                            onMouseDown={handleMouseDown}
                            onMousemove={handleMouseMove}
                            onMouseup={handleMouseUp}

                            onTouchStart={handleMouseDown}
                            onTouchMove={handleMouseMove}
                            onTouchEnd={handleMouseUp}
                        >

                            {/* EXISTING */}
                            <Layer>
                                {existing.map(sig => (
                                    <ExistingImage key={sig.id} sig={sig} />
                                ))}
                            </Layer>


                            {/* DRAW */}
                            <Layer ref={drawLayerRef}>

                                {lines.map((line, i) => (
                                    <Line
                                        key={i}
                                        points={line.points}
                                        stroke={line.tool === "eraser" ? "black" : line.color}
                                        strokeWidth={line.size}
                                        lineCap="round"
                                        lineJoin="round"
                                        globalCompositeOperation={
                                            line.tool === "eraser"
                                                ? "destination-out"
                                                : "source-over"
                                        }
                                    />
                                ))}

                                {img && (
                                    <>
                                        <KImage
                                            ref={imgRef}
                                            image={img}
                                            x={sig.x}
                                            y={sig.y}
                                            width={sig.width}
                                            height={w && h ? (sig.width * h) / w : sig.height}
                                            draggable
                                            onClick={() => {
                                                trRef.current.nodes([imgRef.current]);
                                                trRef.current.getLayer().batchDraw();
                                            }}
                                        />
                                        <Transformer ref={trRef} />
                                    </>
                                )}


                            </Layer>

                        </Stage>

                    </div>

                </div>
            )}
        </main>
    );
}